import React, { useEffect, useState } from "react";
import { getHomepage, saveHomepage, listMedia, mediaUrl } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { toast } from "sonner";
import { Save, ArrowUp, ArrowDown, Plus, Trash2, Image as ImageIcon } from "lucide-react";

const DEFAULT_KEYS_LABELS = {
  hero: "Hero",
  featured_books: "Featured Books",
  green_guru: "Green Guru Knowledge Series",
  why: "Why These Books Exist",
  research_approach: "Research Approach",
  author_journey: "Author's Journey",
  watch: "Watch & Explore",
  join_mission: "Join the Mission",
  articles: "Latest Articles",
  events: "Events",
  newsletter: "Newsletter",
  final_cta: "Final CTA",
};

export default function AdminHomepage() {
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [portraits, setPortraits] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getHomepage().then(d => { if (!cancelled) setSections(d?.sections || []); }).catch(() => {});
    // Show every non-branding kind so publisher-uploaded images (kind=other
    // or any research/hero image) are all selectable, not just the narrow set.
    Promise.all([
      listMedia({ kind: "research_image" }).catch(() => []),
      listMedia({ kind: "other" }).catch(() => []),
      listMedia({ kind: "book_cover" }).catch(() => []),
    ]).then(([a, b, c]) => { if (!cancelled) setImages([...(a || []), ...(b || []), ...(c || [])]); });
    listMedia({ kind: "author_photo" }).then(d => { if (!cancelled) setPortraits(d || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setSection = (i, patch) => setSections(s => s.map((x, j) => j === i ? { ...x, ...patch } : x));
  const move = (i, dir) => {
    setSections(s => {
      const next = [...s];
      const j = i + dir;
      if (j < 0 || j >= next.length) return s;
      [next[i], next[j]] = [next[j], next[i]];
      return next.map((x, k) => ({ ...x, order: k }));
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const withOrder = sections.map((s, i) => ({ ...s, order: i }));
      await saveHomepage({ sections: withOrder });
      toast.success("Homepage saved. Reload the public site to see updates.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-homepage-page">
      <PageHeader eyebrow="Homepage" title="Homepage sections" note="Toggle sections, reorder them, and edit headings, body copy and images. Sections with 'Enabled' unchecked are removed from the public homepage." />
      <div className="mt-8 space-y-4">
        {sections.map((s, i) => (
          <SectionCard
            key={s.key}
            index={i}
            total={sections.length}
            section={s}
            onChange={patch => setSection(i, patch)}
            onMove={dir => move(i, dir)}
            images={images}
            portraits={portraits}
          />
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-maroon mt-8" data-testid="homepage-save-btn">
        <Save size={12} /> {saving ? "Saving…" : "Save homepage"}
      </button>
    </div>
  );
}

const SectionCard = ({ index, total, section, onChange, onMove, images, portraits }) => {
  const [expanded, setExpanded] = useState(false);
  const enabled = section.enabled !== false;
  return (
    <div className="border bg-white" style={{ borderColor: "var(--gi-border)" }} data-testid={`homepage-section-${section.key}`}>
      <div className="px-5 py-3 border-b flex items-center justify-between gap-4 flex-wrap" style={{ borderColor: "var(--gi-border)" }}>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={e => onChange({ enabled: e.target.checked })} data-testid={`homepage-enable-${section.key}`} /> Enabled
          </label>
          <span className="font-heading text-base" style={{ color: enabled ? "var(--gi-maroon)" : "var(--gi-muted)" }}>
            {DEFAULT_KEYS_LABELS[section.key] || section.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-ghost" onClick={() => onMove(-1)} disabled={index === 0} data-testid={`homepage-up-${section.key}`}><ArrowUp size={12} /></button>
          <button type="button" className="btn-ghost" onClick={() => onMove(1)} disabled={index === total - 1} data-testid={`homepage-down-${section.key}`}><ArrowDown size={12} /></button>
          <button type="button" className="btn-outline" onClick={() => setExpanded(v => !v)} data-testid={`homepage-toggle-${section.key}`}>{expanded ? "Collapse" : "Edit"}</button>
        </div>
      </div>
      {expanded && <SectionEditor section={section} onChange={onChange} images={images} portraits={portraits} />}
    </div>
  );
};

const SectionEditor = ({ section, onChange, images, portraits }) => {
  const set = (k, v) => onChange({ [k]: v });
  switch (section.key) {
    case "hero":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading (use <br /> for line break)" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Subheading" rows={3} value={section.subheading} onChange={v => set("subheading", v)} />
          <MediaPicker label="Primary hero image" value={section.hero_primary_image_url} onChange={v => set("hero_primary_image_url", v)} options={images} testId="homepage-hero-primary" />
          <Text label="Primary image alt text" value={section.hero_primary_image_alt} onChange={v => set("hero_primary_image_alt", v)} />
          <MediaPicker label="Manuscript inset image" value={section.hero_manuscript_image_url} onChange={v => set("hero_manuscript_image_url", v)} options={images} testId="homepage-hero-manuscript" />
          <MediaPicker label="Map inset image" value={section.hero_map_image_url} onChange={v => set("hero_map_image_url", v)} options={images} testId="homepage-hero-map" />
          <CtaList label="Hero CTAs" value={section.ctas} onChange={v => set("ctas", v)} testPrefix="hero-cta" />
        </div>
      );
    case "featured_books":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Note under heading" rows={2} value={section.note} onChange={v => set("note", v)} />
          <p className="text-xs" style={{ color: "var(--gi-muted)" }}>Books shown here are those flagged "Featured on homepage" in Admin → Books.</p>
        </div>
      );
    case "green_guru":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Body" rows={3} value={section.body} onChange={v => set("body", v)} />
          <Text label="CTA label" value={section.cta_label} onChange={v => set("cta_label", v)} />
          <Text label="CTA link (/path)" value={section.cta_to} onChange={v => set("cta_to", v)} />
        </div>
      );
    case "why":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Body" rows={2} value={section.body} onChange={v => set("body", v)} />
          <ObjectList label="Why cards" value={section.cards} onChange={v => set("cards", v)} fields={[
            { key: "index", label: "Index (e.g. 01)" },
            { key: "title", label: "Title" },
            { key: "body", label: "Body", textarea: true },
          ]} />
        </div>
      );
    case "research_approach":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <ObjectList label="Pillars" value={section.pillars} onChange={v => set("pillars", v)} fields={[
            { key: "title", label: "Title" },
            { key: "note", label: "Note", textarea: true },
          ]} />
          <TextArea label="Editorial disclaimer" rows={3} value={section.disclaimer} onChange={v => set("disclaimer", v)} />
        </div>
      );
    case "author_journey":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Body" rows={3} value={section.body} onChange={v => set("body", v)} />
          <MediaPicker label="Author portrait" value={section.portrait_url} onChange={v => set("portrait_url", v)} options={portraits} testId="homepage-author-portrait" />
          <ObjectList label="Journey nodes" value={section.nodes} onChange={v => set("nodes", v)} fields={[
            { key: "title", label: "Title" },
            { key: "note", label: "Description", textarea: true },
          ]} />
        </div>
      );
    case "watch":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Body" rows={3} value={section.body} onChange={v => set("body", v)} />
          <p className="text-xs" style={{ color: "var(--gi-muted)" }}>Videos shown here are pulled from Admin → Videos (only published items).</p>
        </div>
      );
    case "join_mission":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Body" rows={3} value={section.body} onChange={v => set("body", v)} />
          <ObjectList label="Participation cards" value={section.cards} onChange={v => set("cards", v)} fields={[
            { key: "id", label: "ID (member, volunteer, partner, support)" },
            { key: "index", label: "Index (01…)" },
            { key: "title", label: "Title" },
            { key: "body", label: "Body", textarea: true },
          ]} />
        </div>
      );
    case "articles":
    case "events":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <p className="text-xs" style={{ color: "var(--gi-muted)" }}>Items shown here come from Admin → {section.key === "articles" ? "Articles" : "Events"} (only published items).</p>
        </div>
      );
    case "newsletter":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <Text label="Heading" value={section.heading} onChange={v => set("heading", v)} />
          <TextArea label="Body" rows={3} value={section.body} onChange={v => set("body", v)} />
        </div>
      );
    case "final_cta":
      return (
        <div className="p-5 grid gap-3">
          <Text label="Eyebrow" value={section.eyebrow} onChange={v => set("eyebrow", v)} />
          <TextArea label="Heading (line break allowed with <br />)" rows={2} value={section.heading} onChange={v => set("heading", v)} />
          <CtaList label="CTAs" value={section.ctas} onChange={v => set("ctas", v)} testPrefix="fcta" />
        </div>
      );
    default:
      return <div className="p-5 text-sm" style={{ color: "var(--gi-muted)" }}>No custom fields for this section yet.</div>;
  }
};

const Text = ({ label, value, onChange }) => (
  <div>
    <label className="gi-label">{label}</label>
    <input className="gi-input" value={value || ""} onChange={e => onChange(e.target.value)} />
  </div>
);
const TextArea = ({ label, value, onChange, rows = 3 }) => (
  <div>
    <label className="gi-label">{label}</label>
    <textarea rows={rows} className="gi-input" value={value || ""} onChange={e => onChange(e.target.value)} />
  </div>
);

const MediaPicker = ({ label, value, onChange, options, testId }) => (
  <div data-testid={testId}>
    <label className="gi-label">{label}</label>
    <div className="grid gap-3 grid-cols-4">
      <label className="border p-2 cursor-pointer flex flex-col items-center gap-1 text-xs" style={{ borderColor: !value ? "var(--gi-maroon)" : "var(--gi-border)" }}>
        <input type="radio" checked={!value} onChange={() => onChange("")} className="sr-only" />
        <div className="w-full aspect-square bg-[var(--gi-ivory-2)] flex items-center justify-center"><ImageIcon size={20} style={{ color: "var(--gi-muted)" }} /></div>
        <span>Default</span>
      </label>
      {(options || []).slice(0, 7).map(o => (
        <label key={o.id} className="border p-2 cursor-pointer flex flex-col items-center gap-1 text-xs" style={{ borderColor: value === o.url ? "var(--gi-maroon)" : "var(--gi-border)" }}>
          <input type="radio" checked={value === o.url} onChange={() => onChange(o.url)} className="sr-only" />
          <img src={mediaUrl(o.url)} alt={o.alt_text || o.filename} className="w-full aspect-square object-cover" />
          <span className="truncate w-full">{o.filename}</span>
        </label>
      ))}
    </div>
  </div>
);

const CtaList = ({ label, value, onChange, testPrefix }) => {
  const list = value || [];
  const setAt = (i, patch) => onChange(list.map((c, j) => j === i ? { ...c, ...patch } : c));
  return (
    <div>
      <label className="gi-label">{label}</label>
      <div className="space-y-2">
        {list.map((c, i) => (
          <div key={`${testPrefix}-${i}`} className="grid gap-2 sm:grid-cols-[1fr_1fr_140px_auto]">
            <input className="gi-input" placeholder="Label" value={c.label || ""} onChange={e => setAt(i, { label: e.target.value })} />
            <input className="gi-input" placeholder="/path or #anchor" value={c.to || ""} onChange={e => setAt(i, { to: e.target.value })} />
            <select className="gi-input" value={c.variant || "ghost"} onChange={e => setAt(i, { variant: e.target.value })}>
              <option value="maroon">Solid maroon</option>
              <option value="gold">Solid gold</option>
              <option value="outline">Outline</option>
              <option value="ghost">Ghost</option>
            </select>
            <button type="button" className="btn-ghost" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
          </div>
        ))}
        <button type="button" className="btn-ghost" onClick={() => onChange([...list, { label: "", to: "", variant: "ghost" }])}><Plus size={12} /> Add CTA</button>
      </div>
    </div>
  );
};

const ObjectList = ({ label, value, onChange, fields }) => {
  const list = value || [];
  const setAt = (i, patch) => onChange(list.map((c, j) => j === i ? { ...c, ...patch } : c));
  return (
    <div>
      <label className="gi-label">{label}</label>
      <div className="space-y-3">
        {list.map((row, i) => (
          <div key={`row-${i}`} className="border p-3" style={{ borderColor: "var(--gi-border)" }}>
            <div className="grid gap-2">
              {fields.map(f => (
                f.textarea ? (
                  <div key={f.key}>
                    <label className="gi-label">{f.label}</label>
                    <textarea rows={2} className="gi-input" value={row[f.key] || ""} onChange={e => setAt(i, { [f.key]: e.target.value })} />
                  </div>
                ) : (
                  <div key={f.key}>
                    <label className="gi-label">{f.label}</label>
                    <input className="gi-input" value={row[f.key] || ""} onChange={e => setAt(i, { [f.key]: e.target.value })} />
                  </div>
                )
              ))}
            </div>
            <button type="button" className="btn-ghost mt-2" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 size={12} /> Remove</button>
          </div>
        ))}
        <button type="button" className="btn-outline" onClick={() => onChange([...list, {}])}><Plus size={12} /> Add row</button>
      </div>
    </div>
  );
};
